import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AlertComponent,
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  ColComponent,
  ContainerComponent,
  FormLabelDirective,
  FormSelectDirective,
  PageItemDirective,
  PageLinkDirective,
  PaginationComponent,
  RowComponent,
  TableDirective
} from '@coreui/angular-pro';
import { UserService } from 'app';
import { I18nService, TranslatePipe } from 'i18n';
import { Booking, BookingCategory, BookingsFilters, BookingType, Logger } from 'shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-bookings-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardBodyComponent,
    ButtonDirective,
    TableDirective,
    BadgeComponent,
    PaginationComponent,
    PageItemDirective,
    PageLinkDirective,
    AlertComponent,
    FormSelectDirective,
    FormLabelDirective
  ],
  templateUrl: './bookings-page.component.html',
  styleUrls: ['./bookings-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookingsPageComponent {
  private logger = new Logger('BookingsPageComponent');
  private userService = inject(UserService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private i18n = inject(I18nService);

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  bookings = signal<Booking[]>([]);
  loading = signal(false);
  exporting = signal(false);
  error = signal<string | null>(null);

  filters = signal<BookingsFilters>({
    page: 1,
    per_page: 20,
    booking_type: ''
  });

  totalPages = signal(0);
  totalCount = signal(0);

  // Filter options
  bookingTypeOptions = signal([
    { value: '', label: 'bookings.filter.all_types' },
    { value: 'transaction' as BookingType, label: 'bookings.filter.transactions' },
    { value: 'order' as BookingType, label: 'bookings.filter.orders' }
  ]);

  perPageOptions = [10, 20, 50];

  // ────────────────────────────────────────────────
  // Computed pagination helpers
  // ────────────────────────────────────────────────
  visiblePages = computed(() => {
    const current = this.filters().page || 1;
    const total = this.totalPages();
    const maxVisible = 5;
    const pages: number[] = [];

    if (total <= maxVisible) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }

    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  });

  showingStart = computed(() => {
    const f = this.filters();
    return ((f.page || 1) - 1) * (f.per_page || 20) + 1;
  });

  showingEnd = computed(() => {
    const f = this.filters();
    const end = (f.page || 1) * (f.per_page || 20);
    return Math.min(end, this.totalCount());
  });

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    // Reload bookings when filters change
    effect(() => {
      this.filters();
      this.loadBookings();
    });
  }

  // ────────────────────────────────────────────────
  // Core actions
  // ────────────────────────────────────────────────
  private loadBookings(): void {
    this.loading.set(true);
    this.error.set(null);

    this.userService.getBookings(this.filters())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.bookings.set(response.bookings);
            this.totalPages.set(response.total_pages);
            this.totalCount.set(response.total_count);
            this.applyAvailableTypes(response.available_booking_types);
            this.loading.set(false);
          },
          error: err => {
            this.logger.error('Failed to load bookings:', err);
            this.error.set('Failed to load bookings');
            this.loading.set(false);
          }
    });
  }

  /**
   * Rebuilds the type filter from what the brand actually shows. Each brand carries
   * only part of the shared statement (orders, ledger movements, or both), and a
   * filter for the missing side would look like a bug: it always returns nothing.
   */
  private applyAvailableTypes(types?: BookingType[]): void {
    if (!types) return;

    const options: { value: string; label: string }[] = [{ value: '', label: 'bookings.filter.all_types' }];
    if (types.includes('transaction')) options.push({ value: 'transaction', label: 'bookings.filter.transactions' });
    if (types.includes('order')) options.push({ value: 'order', label: 'bookings.filter.orders' });
    this.bookingTypeOptions.set(options);
  }

  updateFilterProperty<K extends keyof BookingsFilters>(
    key: K,
    value: BookingsFilters[K] | null | undefined
  ): void {
    this.filters.update(current => {
      const newValue = value === '' || value == null ? undefined : value;
      return {
        ...current,
        [key]: newValue,
        page: 1 // reset pagination
      };
    });
  }

  applyFilters(): void {
    this.filters.update(f => ({ ...f, page: 1 }));
  }

  resetFilters(): void {
    this.filters.set({
      page: 1,
      per_page: 20,
      booking_type: ''
    });
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.filters.update(f => ({ ...f, page }));
  }

  changePerPage(): void {
    this.filters.update(f => ({ ...f, page: 1 }));
  }

  navigateToOrder(orderId: number): void {
    const lang = this.i18n.getLanguage();
    this.router.navigate(['/', lang, 'orders', orderId]);
  }

  // ────────────────────────────────────────────────
  // UI Helpers
  // ────────────────────────────────────────────────
  getCategoryBadgeClass(category: BookingCategory): string {
    switch (category) {
      case 'user_transfer': return 'info';
      case 'system_commission': return 'success';
      case 'pool_payout': return 'primary';
      case 'legacy_notes': return 'primary';
      case 'community_points': return 'success';
      case 'leadership_bonus': return 'success';
      case 'pack_hero': return 'success';
      case 'payout': return 'warning';
      case 'order': return 'secondary';
      case 'membership_grant': return 'info';
      case 'account_payment': return 'light';
      default: return 'dark';
    }
  }

  getCategoryLabel(category: BookingCategory): string {
    const labels: Record<BookingCategory, string> = {
      user_transfer: 'bookings.category.user_transfer',
      system_commission: 'bookings.category.system_commission',
      pool_payout: 'bookings.category.pool_payout',
      legacy_notes: 'bookings.category.legacy_notes',
      community_points: 'bookings.category.community_points',
      leadership_bonus: 'bookings.category.leadership_bonus',
      pack_hero: 'bookings.category.pack_hero',
      payout: 'bookings.category.payout',
      order: 'bookings.category.order',
      refund: 'bookings.category.refund',
      membership_grant: 'bookings.category.membership_grant',
      account_payment: 'bookings.category.account_payment',
      other: 'bookings.category.other'
    };
    return labels[category] || 'bookings.category.other';
  }

  getSubjectLabel(subject: string | undefined): string {
    if (!subject) return '';
    const known = [
      'com_card', 'com_lic', 'pooloffice_distribution',
      'pool_payout', 'payout', 'user_transfer',
      'order_payment', 'account_payment'
    ];
    return known.includes(subject) ? `bookings.subject.${subject}` : '';
  }

  getAmountClass(amountDisplay: string): string {
    if (amountDisplay.startsWith('+')) return 'text-success';
    if (amountDisplay.startsWith('-')) return 'text-danger';
    return '';
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  clearMessages(): void {
    this.error.set(null);
  }

  exportToExcel(): void {
    this.exporting.set(true);
    this.userService.exportBookings()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `bookings_${new Date().toISOString().slice(0, 10)}.xlsx`;
          link.click();
          window.URL.revokeObjectURL(url);
          this.exporting.set(false);
        },
        error: (err) => {
          this.logger.error('Failed to export bookings:', err);
          this.exporting.set(false);
          this.error.set('Failed to export bookings');
        }
      });
  }
}